import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import CreateListingPage from './pages/CreateListingPage';
import InventoryPage from './pages/InventoryPage';
import MaterialsPage from './pages/MaterialsPage';
import AlbaranScanPage from './pages/AlbaranScanPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import ThreeDPage from './pages/ThreeDPage';
import AIAgentPage from './pages/AIAgentPage';
import MarketplacePage from './pages/MarketplacePage';
import CommandCenterPage from './pages/CommandCenterPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import GDPRCompliance from './pages/GDPRCompliance';
import CreditsPage from './pages/CreditsPage';
import { apiFetch, getApiBase } from './utils/api';

const getApiUrl = () => {
    const base = getApiBase();
    return base || window.location.origin;
};

const isAppDomain = () => {
    const hostname = window.location.hostname;
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('token') || urlParams.has('shop')) return true;

    if (hostname === 'trycommerium.com' || hostname === 'www.trycommerium.com' || hostname === 'localhost' || hostname === '127.0.0.1') {
        return false;
    }

    return true;
};

const isEmbeddedShopify = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(urlParams.get('shop') && urlParams.get('host'));
};

interface User {
    id: string;
    email: string;
    name: string;
    role: string;
}

function App() {
    const initialParams = new URLSearchParams(window.location.search);
    const embedded = isEmbeddedShopify();
    const oauthTokenInUrl = initialParams.get('token');

    const [user, setUser] = useState<User | null>(() => {
        if (embedded && !oauthTokenInUrl) {
            return null;
        }

        const stored = localStorage.getItem('artisan_user');
        if (stored) {
            return JSON.parse(stored);
        }

        if (oauthTokenInUrl) {
            try {
                const payload = JSON.parse(atob(oauthTokenInUrl.split('.')[1]));
                return {
                    id: payload.id,
                    email: payload.email,
                    name: payload.name || payload.email.split('@')[0],
                    role: payload.role || 'ARTISAN',
                };
            } catch {
                return null;
            }
        }

        return null;
    });

    const [onApp, setOnApp] = useState(isAppDomain());
    const [authChecking, setAuthChecking] = useState(embedded && !oauthTokenInUrl);

    useEffect(() => {
        const handleGlobalDragOver = (e: DragEvent) => e.preventDefault();
        const handleGlobalDrop = (e: DragEvent) => e.preventDefault();
        window.addEventListener('dragover', handleGlobalDragOver);
        window.addEventListener('drop', handleGlobalDrop);
        return () => {
            window.removeEventListener('dragover', handleGlobalDragOver);
            window.removeEventListener('drop', handleGlobalDrop);
        };
    }, []);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const shop = urlParams.get('shop');
        const host = urlParams.get('host');
        const apiUrl = getApiUrl();

        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const userData: User = {
                    id: payload.id,
                    email: payload.email,
                    name: payload.name || payload.email.split('@')[0],
                    role: payload.role || 'ARTISAN',
                };

                handleLogin(userData, token);

                if (shop || payload.shop) {
                    localStorage.setItem('shopify_shop_domain', shop || payload.shop);
                }

                urlParams.delete('token');
                window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
                setOnApp(true);
                setAuthChecking(false);
            } catch (e) {
                console.error('Auth sync failed', e);
                setAuthChecking(false);
            }
            return;
        }

        if (shop && host) {
            localStorage.setItem('shopify_shop_domain', shop);

            const redirectToOAuth = () => {
                const installUrl =
                    `${apiUrl}/api/shopify/install?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(host)}`;
                // Shopify's OAuth grant screen sends X-Frame-Options: DENY and
                // cannot render inside the embedded admin iframe. Break out to
                // the top-level window. App Bridge (loaded in index.html)
                // intercepts window.open(_top) so this works without a user
                // gesture; fall back to a normal navigation when not embedded.
                if (window.top && window.top !== window.self) {
                    window.open(installUrl, '_top');
                } else {
                    window.location.href = installUrl;
                }
            };

            fetch(`${apiUrl}/api/shopify/status?shop=${encodeURIComponent(shop)}`)
                .then(async (r) => {
                    if (r.status >= 500) {
                        // Transient backend/DB cold start: recover via OAuth
                        // (which (re)establishes the connection) instead of
                        // dead-ending on an error screen after install.
                        redirectToOAuth();
                        return;
                    }
                    const status = await r.json();
                    if (!status.connected) {
                        redirectToOAuth();
                        return;
                    }

                    setUser({
                        id: 'shopify-embedded',
                        email: 'merchant@shopify.com',
                        name: 'Merchant',
                        role: 'ARTISAN',
                    });
                    setAuthChecking(false);

                    // Shopify appends charge_id/plan_handle to whatever URL a plan's
                    // Redirect URL points to — which may not be /credits (e.g. a plan
                    // configured before that convention existed). Reconcile billing
                    // here too so a stale plan can't survive a redirect landing on root.
                    if (urlParams.has('charge_id') || urlParams.has('plan_handle')) {
                        apiFetch('/api/billing/sync', { method: 'POST', body: JSON.stringify({}) })
                            .catch((err) => console.error('Billing sync on root failed:', err));
                        urlParams.delete('charge_id');
                        urlParams.delete('plan_handle');
                        window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
                    }
                })
                .catch(() => redirectToOAuth());
            return;
        }

        setAuthChecking(false);
    }, []);

    const handleLogin = (userData: User, token: string) => {
        localStorage.setItem('artisan_token', token);
        localStorage.setItem('artisan_user', JSON.stringify(userData));
        setUser(userData);
    };

    const handleLogout = () => {
        localStorage.removeItem('artisan_token');
        localStorage.removeItem('artisan_user');
        setUser(null);
    };

    const policyRoutes = (
        <>
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/gdpr" element={<GDPRCompliance />} />
        </>
    );

    if (authChecking) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                fontFamily: 'system-ui, sans-serif',
                color: '#444',
            }}>
                Connecting to Shopify…
            </div>
        );
    }

    return (
        <BrowserRouter>
            <Routes>
                {policyRoutes}

                {!onApp ? (
                    <>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </>
                ) : (
                    <>
                        {!user ? (
                            <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
                        ) : (
                            <>
                                <Route path="/" element={<Layout user={user} onLogout={handleLogout}><DashboardPage /></Layout>} />
                                <Route path="/create" element={<Layout user={user} onLogout={handleLogout}><CreateListingPage /></Layout>} />
                                <Route path="/inventory" element={<Layout user={user} onLogout={handleLogout}><InventoryPage /></Layout>} />
                                <Route path="/materials" element={<Layout user={user} onLogout={handleLogout}><MaterialsPage /></Layout>} />
                                <Route path="/albaran" element={<Layout user={user} onLogout={handleLogout}><AlbaranScanPage /></Layout>} />
                                <Route path="/settings" element={<Layout user={user} onLogout={handleLogout}><SettingsPage /></Layout>} />
                                <Route path="/3d" element={<Layout user={user} onLogout={handleLogout}><ThreeDPage /></Layout>} />
                                <Route path="/ai-agent" element={<Layout user={user} onLogout={handleLogout}><AIAgentPage /></Layout>} />
                                <Route path="/marketplace" element={<Layout user={user} onLogout={handleLogout}><MarketplacePage /></Layout>} />
                                <Route path="/command-center" element={<Layout user={user} onLogout={handleLogout}><CommandCenterPage /></Layout>} />
                                <Route path="/credits" element={<Layout user={user} onLogout={handleLogout}><CreditsPage /></Layout>} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </>
                        )}
                    </>
                )}
            </Routes>

            <ToastContainer position="top-center" autoClose={3000} />
        </BrowserRouter>
    );
}

export default App;
